import React, { useState, useCallback } from 'react';
import { Heading, Text, Button, Flex } from '@vibe/core';
import { useReportStore } from '../../stores/reportStore';
import type { BoardResourcesReportResponse } from '../../api/report.api';
import { createBookmark } from '../../api/report.api';
import BoardResourcesExportButton from './BoardResourcesExportButton';
import BookmarkDialog from './BookmarkDialog';
import { formatHours } from '../../utils/timesheet-capacity';

interface Props {
  data: BoardResourcesReportResponse;
}

const BoardResourcesHeader: React.FC<Props> = ({ data }) => {
  const { currentConfig, setView, setBookmarks, bookmarks } = useReportStore();
  const [showBookmarkDialog, setShowBookmarkDialog] = useState(false);
  const [bookmarking, setBookmarking] = useState(false);

  const handleBookmark = useCallback(
    async (name: string) => {
      if (!currentConfig) return;
      setBookmarking(true);
      try {
        const bookmark = await createBookmark({
          name,
          reportType: 'board_resources_forecast',
          config: currentConfig,
        });
        setBookmarks([...bookmarks, bookmark]);
        setShowBookmarkDialog(false);
      } catch (err) {
        console.error('Failed to bookmark report:', err);
      } finally {
        setBookmarking(false);
      }
    },
    [currentConfig, bookmarks, setBookmarks],
  );

  return (
    <div className="rp-report-header">
      <Flex
        justify={Flex.justify.SPACE_BETWEEN}
        align={Flex.align.CENTER}
      >
        <div>
          <Heading type={Heading.types.H2}>
            Board Resources Forecast
          </Heading>
          <Flex gap={Flex.gaps.MEDIUM} style={{ marginTop: 4 }}>
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              {data.summary.totalBoards} boards
            </Text>
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              {data.summary.totalScheduledHours === 0 ? '0' : formatHours(data.summary.totalScheduledHours)}h total scheduled
            </Text>
          </Flex>
        </div>
        <Flex gap={Flex.gaps.SMALL}>
          <BoardResourcesExportButton data={data} />
          <Button
            kind={Button.kinds.SECONDARY}
            size={Button.sizes.SMALL}
            onClick={() => setShowBookmarkDialog(true)}
          >
            Bookmark
          </Button>
          <Button
            kind={Button.kinds.SECONDARY}
            size={Button.sizes.SMALL}
            onClick={() => setView('config')}
          >
            Reconfigure
          </Button>
          <Button
            kind={Button.kinds.TERTIARY}
            size={Button.sizes.SMALL}
            onClick={() => setView('gallery')}
          >
            Back to Reports
          </Button>
        </Flex>
      </Flex>

      {showBookmarkDialog && (
        <BookmarkDialog
          saving={bookmarking}
          onSave={handleBookmark}
          onClose={() => setShowBookmarkDialog(false)}
        />
      )}
    </div>
  );
};

export default BoardResourcesHeader;
